"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { MessageSquare, X, Send, Mail, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useTranslation } from "@/lib/hooks/useTranslation";
import { useAuthStore } from "@/lib/store";
import { feedback } from "@/lib/api";
import { toast } from "sonner";

// Discord icon component
function DiscordIcon(t0) {
  const $ = _c(3);
  const {
    className
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== className) {
    t2 = <svg className={className} viewBox="0 0 24 24" fill="currentColor">{t1}</svg>;
    $[1] = className;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}

// X (Twitter) icon
function XIcon(t0) {
  const $ = _c(3);
  const {
    className
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== className) {
    t2 = <svg className={className} viewBox="0 0 24 24" fill="currentColor">{t1}</svg>;
    $[1] = className;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
export function FeedbackWidget() {
  const {
    locale
  } = useTranslation();
  const {
    accessToken
  } = useAuthStore();
  const [isOpen, setIsOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [isSending, setIsSending] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!feedbackText.trim()) return;
    setIsSending(true);
    try {
      // Send to backend (email + Discord)
      await feedback.submitBugReport({
        description: feedbackText.trim(),
        page_url: window.location.href
      }, accessToken || undefined);
      toast.success(locale === "fr" ? "Merci pour ton feedback ! 🙏" : "Thanks for your feedback! 🙏");
      setFeedbackText("");
      setIsOpen(false);
    } catch (error) {
      console.error("Feedback error:", error);
      toast.error(locale === "fr" ? "Erreur lors de l'envoi" : "Error sending feedback");
    } finally {
      setIsSending(false);
    }
  };
  return <>
      {/* Floating button */}
      <button onClick={() => setIsOpen(true)} className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 transition-all hover:scale-105" aria-label="Feedback">
        <MessageSquare className="h-6 w-6" />
      </button>

      {/* Modal */}
      {isOpen && <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsOpen(false)} />

          {/* Content */}
          <div className="relative w-full max-w-md rounded-xl bg-background border shadow-2xl animate-in slide-in-from-bottom-4 duration-300">
            {/* Header */}
            <div className="flex items-center justify-between border-b px-4 py-3">
              <h3 className="font-semibold">
                {locale === "fr" ? "Ton feedback" : "Your feedback"}
              </h3>
              <button onClick={() => setIsOpen(false)} className="rounded-full p-1 hover:bg-muted transition-colors">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-4 space-y-4">
              <p className="text-sm text-muted-foreground">
                {locale === "fr" ? "Une idée, un bug, une suggestion ? On t'écoute !" : "An idea, a bug, a suggestion? We're listening!"}
              </p>

              <form onSubmit={handleSubmit} className="space-y-3">
                <Textarea placeholder={locale === "fr" ? "Écris ton feedback ici..." : "Write your feedback here..."} value={feedbackText} onChange={e_0 => setFeedbackText(e_0.target.value)} rows={4} className="resize-none" />
                <Button type="submit" className="w-full" disabled={!feedbackText.trim() || isSending}>
                  {isSending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                  {locale === "fr" ? "Envoyer" : "Send"}
                </Button>
              </form>

              {/* Divider */}
              <div className="relative py-2">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    {locale === "fr" ? "Ou rejoins-nous" : "Or join us"}
                  </span>
                </div>
              </div>

              {/* Links */}
              <div className="flex gap-2">
                <a href="https://discord.gg/hbevuxEKCq" target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-2 rounded-lg border py-2.5 text-sm font-medium hover:bg-muted transition-colors">
                  <DiscordIcon className="h-4 w-4" />
                  Discord
                </a>
                <a href="https://x.com/velyq_ai" target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-2 rounded-lg border py-2.5 text-sm font-medium hover:bg-muted transition-colors">
                  <XIcon className="h-4 w-4" />
                  Twitter
                </a>
                <a href="mailto:contact@velyq.com" className="flex-1 flex items-center justify-center gap-2 rounded-lg border py-2.5 text-sm font-medium hover:bg-muted transition-colors">
                  <Mail className="h-4 w-4" />
                  Email
                </a>
              </div>
            </div>
          </div>
        </div>}
    </>;
}